import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Adds every item of the selected bundle tier to the cart in one request.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} addButton
 * @property {HTMLInputElement[]} [tiers]
 * @property {HTMLElement} [status]
 *
 * @extends {Component<Refs>}
 */
class BundlePicker extends Component {
  requiredRefs = ['addButton'];

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    this.#updateAvailability();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /**
   * Collects the variant chosen for each item in the selected tier, merged by variant.
   * @returns {{ id: string, quantity: number }[]}
   */
  #getItems() {
    const selected = this.refs.tiers?.find((tier) => tier.checked);
    const tier = selected?.closest('.bundle-picker__tier');
    if (!selected || !tier) return [];

    /** @type {Map<string, number>} */
    const quantities = new Map();
    const selects = tier.querySelectorAll('[data-bundle-item]');

    if (selects.length > 0) {
      for (const select of selects) {
        if (!(select instanceof HTMLSelectElement) || !select.value) continue;
        quantities.set(select.value, (quantities.get(select.value) ?? 0) + 1);
      }
    } else {
      const fallback = tier.querySelector('[data-bundle-default-variant]');
      if (fallback instanceof HTMLInputElement && fallback.value) {
        quantities.set(fallback.value, Number(selected.value) || 1);
      }
    }

    const items = Array.from(quantities, ([id, quantity]) => ({ id, quantity }));

    // The optional add-on is one extra line, added only alongside at least one bundle item
    const addon = tier.querySelector('[data-bundle-addon]');
    if (items.length > 0 && addon instanceof HTMLInputElement && addon.checked && addon.value) {
      items.push({ id: addon.value, quantity: 1 });
    }

    return items;
  }

  /**
   * Updates the thumbnail beside the changed dropdown and shows that style in the product gallery.
   * @param {Event} event - The change event.
   */
  handleStyleChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const option = select.selectedOptions[0];
    const thumb = select.closest('.bundle-picker__item')?.querySelector('.bundle-picker__thumb');
    if (thumb instanceof HTMLImageElement && option?.dataset.thumb) {
      thumb.src = option.dataset.thumb;
      thumb.hidden = false;
    }

    const mediaId = option?.dataset.mediaId;
    const slideshow = this.closest('.shopify-section')?.querySelector('media-gallery slideshow-component');
    const slide = mediaId ? slideshow?.querySelector(`slideshow-slide:has([data-media-id="${mediaId}"])`) : null;
    if (slide && slideshow && 'select' in slideshow && typeof slideshow.select === 'function') {
      // Gallery slides carry no slide-id, so select by position
      const index = Array.from(slideshow.querySelectorAll('slideshow-slide')).indexOf(slide);
      if (index >= 0) slideshow.select(index);
    }

    this.#updateAvailability();
  }

  /** Handles a change of the selected bundle tier. */
  handleTierChange() {
    this.#updateAvailability();
  }

  /** Disables the button while any chosen style in the selected tier is sold out. */
  #updateAvailability() {
    const { addButton } = this.refs;
    const tier = this.refs.tiers?.find((input) => input.checked)?.closest('.bundle-picker__tier');
    if (!tier || !addButton.dataset.labelAdd) return;

    let available = true;
    for (const select of tier.querySelectorAll('[data-bundle-item]')) {
      if (select instanceof HTMLSelectElement && select.selectedOptions[0]?.dataset.available === 'false') available = false;
    }

    addButton.disabled = !available;
    addButton.textContent = (available ? addButton.dataset.labelAdd : addButton.dataset.labelSoldOut) ?? '';
  }

  /**
   * Handles the add-to-cart button click.
   * @param {Event} event - The click event.
   */
  async handleAdd(event) {
    event.preventDefault();

    const { addButton, status } = this.refs;
    const items = this.#getItems();
    if (items.length === 0 || addButton.disabled) return;

    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;

    addButton.disabled = true;
    if (status) status.textContent = '';

    const sectionIds = [];
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) sectionIds.push(component.dataset.sectionId);
    }

    const itemCount = items.reduce((total, item) => total + item.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();

      if (result.status) {
        const message = result.description || result.message || '';
        if (status) status.textContent = message;
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        deferred.reject(new Error(message));
        return;
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!cartResponse.ok) throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'bundle-picker',
          sourceId: this.id.toString(),
          itemCount,
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      if (this.#controller === controller) {
        this.#controller = null;
        this.#updateAvailability();
      }
    }
  }
}

if (!customElements.get('bundle-picker')) {
  customElements.define('bundle-picker', BundlePicker);
}
